import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { metricCatalogService, MeasurementDomain, ValueType, Unit } from '@/services/metricCatalogService';
type CatalogType = 'domains' | 'valueTypes' | 'units';
type CatalogItem = MeasurementDomain | ValueType | Unit;

interface SimpleCatalogTabProps {
  type: CatalogType;
  title: string;
  itemLabel: string;
}

export function SimpleCatalogTab({ type, title, itemLabel }: SimpleCatalogTabProps) {
  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [formCode, setFormCode] = useState('');
  const queryClient = useQueryClient();

  const queryKey = ['metricCatalog', type];
  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [...queryKey, search],
    queryFn: async () => {
      switch (type) {
        case 'domains':
          return metricCatalogService.getDomains(search);
        case 'valueTypes':
          return metricCatalogService.getValueTypes(search);
        case 'units':
          return metricCatalogService.getUnits(search);
        default:
          return [];
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: async (code: string) => {
      switch (type) {
        case 'domains':
          return metricCatalogService.createDomain(code);
        case 'valueTypes':
          return metricCatalogService.createValueType(code);
        case 'units':
          return metricCatalogService.createUnit(code);
        default:
          throw new Error(`Unknown catalog type: ${type}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setIsAddModalOpen(false);
      setFormCode('');
    },
  });

  const handleSave = () => {
    if (!formCode.trim()) return;
    createMutation.mutate(formCode.trim());
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{title}</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Gérer les {itemLabel.toLowerCase()} du catalogue métrique
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Rechercher par code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            onClick={() => {
              setIsAddModalOpen(true);
              setFormCode('');
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            <span className="material-icons-outlined text-lg">add</span>
            Ajouter
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg border border-rose-300/40 bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20">
          Erreur lors du chargement.
        </div>
      )}

      <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 dark:bg-[#0B1116] text-slate-500 dark:text-slate-400">
              <tr>
                <th className="text-left font-medium px-5 py-3">ID</th>
                <th className="text-left font-medium px-5 py-3">Code</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={2} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400">
                    Chargement...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400">
                    Aucun {itemLabel.toLowerCase()} trouvé.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-5 py-4 text-slate-700 dark:text-slate-300">{item.id}</td>
                    <td className="px-5 py-4 font-medium text-slate-900 dark:text-white">{item.code}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-[#16202A] rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              {`Ajouter ${itemLabel}`}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Code
                </label>
                <input
                  type="text"
                  value={formCode}
                  onChange={(e) => setFormCode(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Entrez le code"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setFormCode('');
                  }}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={!formCode.trim() || createMutation.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
